// CI/CD trigger check - 2026-08-24
import "./App.scss";
import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import ReactGA from "react-ga4";
import { FontAwesomeIcon as FAIcon } from "@fortawesome/react-fontawesome";

import { setWidth, setHeight, saveScoreData } from "./Store/store";

import AppRouter from "./Components/AppRouter";
import TopNavigation from "./Components/UI/TopNavigation";
import BottomNavigation from "./Components/UI/BottomNavigation";
import NavigationMenu from "./Components/UI/NavigationMenu";
import ClearDataPopup from "./Components/UI/ClearDataPopup";
import ErrorBoundary from "./Components/UI/ErrorBoundary";
import { preloadGames } from "./Components/GameContainer";
import { faMessage } from "@fortawesome/free-solid-svg-icons";

let scoreDataLocal = [];
let levelDataLocal = [];

// RedLake Initilize
// ReactGA.initialize("G-77K5NRYVXE");

// FortBelknap Initilize
// ReactGA.initialize("G-1E00CRMCD2");

// Salish Initialize
ReactGA.initialize("G-ZJYGHY6NP6");

// TODO: Add Pokagon
// TODO: Add WestBank
// TODO: Add Denaakke and Benhti

/**
 * Main App component that serves as the root of the Kids Zone application.
 * Manages game state, score data, level progression, and navigation.
 *
 * @param {Object} props - Component props
 * @param {Array} props.scoreDataProp - Array of score data from persistent storage
 * @param {Array} props.levelDataProp - Array of level progression data from persistent storage
 * @param {Function} props.saveData - Function to save data to persistent storage
 * @param {Object} props.config - Application configuration object
 * @returns {JSX.Element} The main application component
 */
const App = ({ scoreDataProp, levelDataProp, saveData, config }) => {
  const location = useLocation();
  const scoreDataRef = useRef([]);
  const datasetRef = useRef("");
  const dispatch = useDispatch();
  const [scoreData, setScoreData] = useState([]);
  const [levelData, setLevelData] = useState([]);
  const [showTopNav, setShowTopNav] = useState(true);
  const [showClearData, setShowClearData] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [fullStarScores, setFullStarScores] = useState([
    { difficulty: 1, currentScore: 0, totalScore: 0 },
    { difficulty: 2, currentScore: 0, totalScore: 0 },
    { difficulty: 3, currentScore: 0, totalScore: 0 },
    { difficulty: 4, currentScore: 0, totalScore: 0 },
    { difficulty: 5, currentScore: 0, totalScore: 0 },
  ]);
  const [dataset, setDataset] = useState("");

  // Fetch the games' code in the background as soon as the app is up, so
  // opening a game doesn't wait on it (see GameContainer's gameLoaders).
  useEffect(() => {
    const start = () => preloadGames();
    if (window.requestIdleCallback) {
      const id = window.requestIdleCallback(start, { timeout: 1500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(start, 500);
    return () => clearTimeout(id);
  }, []);
  useEffect(() => {
    if (!config.hasLocationSelection) return;
    const params = location.search.replace("?", "").split("&");
    const locationParam = params
      .map((p) => p.split("="))
      .find((p) => p[0] === "location")?.[1];
    setDataset(locationParam === "Minto" ? "b_" : "");
  }, [location]);

  scoreDataRef.current = scoreData;
  datasetRef.current = dataset;

  useEffect(() => {
    const handleResize = () => {
      dispatch(setWidth(window.innerWidth));
      dispatch(setHeight(window.innerHeight));
    };
    window.addEventListener("resize", handleResize);
    if (scoreDataProp.length > 0) {
      let scoreDataToSet = filterScoreData(scoreDataProp);
      dispatch(saveScoreData(scoreDataToSet));
      setScoreData(scoreDataToSet);
      scoreDataDisplayUpdate(scoreDataToSet);
    }
    if (levelDataProp.length > 2) {
      setLevelData(levelDataProp);
    }
    // findSetIDs(); // comment/uncomment when error checking data
    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreDataProp, levelDataProp]);

  /**
   * Save data before window closes.
   * Properly handles beforeunload event to persist score and level data.
   */
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveData(scoreDataRef.current, levelData);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // Also save when component unmounts (navigation within app)
      handleBeforeUnload();
    };
  }, [levelData, saveData]);

  /**
   * Filters score data to remove duplicates and invalid entries.
   * Only keeps entries with valid difficulty scores.
   *
   * @param {Array} data - Raw score data array
   * @returns {Array} Filtered score data with duplicates removed
   */
  const filterScoreData = (data) => {
    let keys = [];
    let filtered = [];
    data.forEach((d) => {
      if (!keys.includes(d.Key)) {
        if (d.difficultyScores.easy.difficulty !== "null") {
          keys.push(d.Key);
          filtered.push(d);
        }
      }
    });
    return filtered;
  };
  /**
   * Updates score data with new game results.
   * Only updates if the new score is better than the existing score.
   *
   * @param {Object|Object[]} newDataOrList - A game's new score (or a list of them), each:
   * @param {string} newData.Key - Unique key identifying the game
   * @param {string} newData.difficulty - Difficulty level of the game
   * @param {number} newData.score - Player's score
   * @param {number} newData.firstStars - First star rating earned
   * @param {number} newData.secondStars - Second star rating earned
   * @param {number} newData.correct - Number of correct answers
   * @param {number} newData.incorrect - Number of incorrect answers
   */
  const updateScoreData = (newDataOrList) => {
    // one score, or several at once (Story Book Corner saves every part of a
    // book: separate calls would each start from the same old scoreData, so
    // only the last would stick)
    const updates = Array.isArray(newDataOrList) ? newDataOrList : [newDataOrList];
    scoreDataLocal = scoreData;
    let newScoreData = scoreDataLocal.map((game) => {
      const newData = updates.find((d) => `${d.Key}${d.gameType}` === game.Key);
      if (newData) {
        let gameScore = game.difficultyScores[newData.difficulty];
        if (
          newData.firstStars + newData.secondStars >=
            gameScore.firstStars + gameScore.secondStars &&
          newData.score >= gameScore.score
        ) {
          return {
            ...game,
            difficultyScores: {
              ...game.difficultyScores,
              [newData.difficulty]: {
                ...game.difficultyScores[newData.difficulty],
                correct: newData.correct,
                firstNextGoals: newData.firstNextGoals,
                firstStars: newData.firstStars,
                incorrect: newData.incorrect,
                score: newData.score,
                secondNextGoals: newData.secondNextGoals,
                secondStars: newData.secondStars,
                stars: newData.stars,
                time: newData.time,
              },
            },
          };
        } else {
          return game;
        }
      } else {
        return game;
      }
    });
    setScoreData(newScoreData);
    scoreDataDisplayUpdate(newScoreData);
    dispatch(saveScoreData(newScoreData));
  };
  /**
   * Updates level progression data based on game completion.
   * Advances player to next level if they've progressed beyond current level.
   *
   * @param {Object} data - Level update data
   * @param {Array|string} data.category - Game category or categories
   * @param {string} data.difficulty - Difficulty level completed
   * @param {number} data.newLevel - New level achieved
   * @param {string} data.gameType - Type of game completed
   */
  const updateLevelData = (data) => {
    levelDataLocal = levelData;
    let categoryLength = data.category.length;
    const updateGame = ({ game, multi }) => {
      return {
        ...game,
        category: multi ? data.category : data.category[0],
        levelEasy:
          data.difficulty === "easy" && data.newLevel > game.levelEasy
            ? data.newLevel
            : game.levelEasy,
        categoryEasy:
          data.difficulty === "easy" && data.newLevel > game.levelEasy
            ? data.category
            : game.categoryEasy,
        levelMedium:
          data.difficulty === "medium" && data.newLevel > game.levelMedium
            ? data.newLevel
            : game.levelMedium,
        categoryMedium:
          data.difficulty === "medium" && data.newLevel > game.levelMedium
            ? data.category
            : game.categoryMedium,
        levelHard:
          data.difficulty === "hard" && data.newLevel > game.levelHard
            ? data.newLevel
            : game.levelHard,
        categoryHard:
          data.difficulty === "hard" && data.newLevel > game.levelHard
            ? data.category
            : game.categoryHard,
      };
    };
    let newLevelData = levelDataLocal.map((game) => {
      let gameCategory = game.category;
      if (typeof gameCategory !== "string") {
        gameCategory = game.category[0];
      }
      if (
        game.gameType === `${categoryLength} Categories` &&
        data.gameType === game.spreadGameType
      ) {
        // Multiple Categories
        return updateGame({ game: game, multi: true });
      } else if (
        data.gameType === game.gameType &&
        data.category[0] === gameCategory
      ) {
        // Solo Categories
        return updateGame({ game: game, multi: false });
      } else {
        return game;
      }
    });
    setLevelData(newLevelData);
  };
  /**
   * Clears all stored game data and reloads the application.
   * Used for resetting player progress.
   */
  const clearStorage = () => {
    saveData([], []);
    // localStorage.clear();
    window.location.reload();
  };
  /**
   * Updates the full star scores display based on current score data.
   * Calculates total possible stars and current stars earned across all difficulties.
   *
   * @param {Array} currentScoreData - Current score data to calculate stars from
   */
  const scoreDataDisplayUpdate = (currentScoreData) => {
    let settingFullStarScores = [
      { difficulty: 1, currentScore: 0, totalScore: 0 },
      { difficulty: 2, currentScore: 0, totalScore: 0 },
      { difficulty: 3, currentScore: 0, totalScore: 0 },
      { difficulty: 4, currentScore: 0, totalScore: 0 },
      { difficulty: 5, currentScore: 0, totalScore: 0 },
    ];
    currentScoreData.forEach((d) => {
      let easyDiffNum = d.difficultyScores.easy.difficulty;
      settingFullStarScores[easyDiffNum - 1].totalScore =
        settingFullStarScores[easyDiffNum - 1].totalScore + 3;
      settingFullStarScores[easyDiffNum - 1].currentScore =
        settingFullStarScores[easyDiffNum - 1].currentScore +
        d.difficultyScores.easy.stars;
      let medDiffNum = d.difficultyScores.medium.difficulty;
      settingFullStarScores[medDiffNum - 1].totalScore =
        settingFullStarScores[medDiffNum - 1].totalScore + 3;
      settingFullStarScores[medDiffNum - 1].currentScore =
        settingFullStarScores[medDiffNum - 1].currentScore +
        d.difficultyScores.medium.stars;
      let hardDiffNum = d.difficultyScores.hard.difficulty;
      settingFullStarScores[hardDiffNum - 1].totalScore =
        settingFullStarScores[hardDiffNum - 1].totalScore + 3;
      settingFullStarScores[hardDiffNum - 1].currentScore =
        settingFullStarScores[hardDiffNum - 1].currentScore +
        d.difficultyScores.hard.stars;
    });
    setFullStarScores(settingFullStarScores);
  };

  /**
   * Toggles the navigation menu with animations.
   * Handles CSS class changes for slide and fade animations.
   */
  const animateMenu = () => {
    let fade = document.getElementById("navmenu_fade");
    let slide = document.getElementById("navmenu_slide");
    if (!showMenu) {
      fade.classList.remove("navmenu_fullscreen_backward");
      fade.classList.add("navmenu_fullscreen_forward");
      slide.classList.remove("navmenu_dropdownmenu_backward");
      slide.classList.add("navmenu_dropdownmenu_forward");
    } else {
      fade.classList.remove("navmenu_fullscreen_forward");
      fade.classList.add("navmenu_fullscreen_backward");
      slide.classList.remove("navmenu_dropdownmenu_forward");
      slide.classList.add("navmenu_dropdownmenu_backward");
    }
    setShowMenu(!showMenu);
  };

  return (
    <div className="center">
      <div className="centerContentContainer">
        {/* Games hide the top nav while playing; bring it back on a crash so
            the child has a way out besides the fallback's own buttons. */}
        <ErrorBoundary
          resetKey={location.pathname + location.search}
          onError={() => setShowTopNav(true)}
        >
          <AppRouter
            levelData={levelData}
            updateLevelData={updateLevelData}
            scoreData={scoreDataRef.current}
            updateScoreData={updateScoreData}
            setShowTopNav={setShowTopNav}
            dataset={datasetRef.current}
            config={config}
          />
        </ErrorBoundary>
      </div>
      <TopNavigation
        setShowClearData={setShowClearData}
        showClearData={showClearData}
        showNav={showTopNav}
        setShowMenu={animateMenu}
        config={config}
      />
      <BottomNavigation fullStarScores={fullStarScores} config={config} />
      {showClearData ? (
        <ClearDataPopup
          clearData={clearStorage}
          setShowClearData={setShowClearData}
        />
      ) : null}
      {config.FeedbackLink !== undefined && config.FeedbackLink.length > 0 ? (
        <div className="feedback_button_container">
          <a
            href="https://forms.gle/NJd1pfbuV8Dv3nrs8"
            className="feedback_button"
          >
            <div className="feedback_text">Submit your Errors Here</div>
            <FAIcon icon={faMessage} size="xl" />
          </a>
        </div>
      ) : null}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 2,
          zIndex: 99999,
          fontSize: 12,
        }}
      >
        v {config.VersionNumber}
      </div>
      <NavigationMenu animateMenu={animateMenu} config={config} />
    </div>
  );
};

export default App;

App.propTypes = {
  scoreDataProp: PropTypes.array,
  levelDataProp: PropTypes.array,
  saveData: PropTypes.func,
  config: PropTypes.object,
};
